import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  ContractVersionsPageDocument,
  ResumeContractsContractDocument,
  ResumeContractsContractVersionFragment,
} from 'generated/resources/graphql'
import { maxBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FirstStep from './FirstStep'

interface Props {
  contractId: number
  nextAvailableDate: string
  onClose: () => void
  setSelectedContractVersion: (contractVersionId: number | undefined) => void
}

const ResumeContractModal = ({
  contractId,
  nextAvailableDate,
  onClose,
  setSelectedContractVersion,
}: Props) => {
  const [activityResumeDate, setActivityResumeDate] = React.useState('')
  const [onboardingEnabled, setOnboardingEnabled] = React.useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<string | undefined>()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const apolloClient = useApolloClient()
  const { company } = useCurrentSession()

  const [resumeContract] = useMutation(ResumeContractsContractDocument, {
    extractorFn: (data) => data.contractsMutations.resumeContractsContract,
  })

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      const resumeDate = stringToDate(activityResumeDate, 'ui')
      const backendDateString = resumeDate ? i18nDateToString(resumeDate, 'backend') : null

      if (!backendDateString) {
        throw new Error('Invalid date selected')
      }

      const result = await resumeContract({
        variables: {
          contractId,
          contractActivityResumeOn: backendDateString,
          contractOnboardingEnabled: onboardingEnabled,
          onboardingProcessId: selectedWorkflow ? Number(selectedWorkflow) : null,
        },
      })

      const handled = handleResult(result, (_errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('contract_versions.header.pause_resume.resume_modal.error'),
          time: 3000,
        })
      })

      if (handled.success) {
        track(trackingEvents.employee.contracts.startActivityPeriodSubmit, {
          start_date: activityResumeDate,
          company_id: company?.id,
        })
        const contractVersions: ResumeContractsContractVersionFragment[] =
          handled.data.contract?.contractVersions ?? []
        const lastVersion = maxBy(contractVersions, 'id')

        // Manually refetch the ContractVersionsPage query to ensure UI updates
        await apolloClient.refetchQueries({
          include: [ContractVersionsPageDocument],
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('contract_versions.header.pause_resume.resume_modal.success'),
          time: 3000,
        })
        if (lastVersion?.id) {
          setSelectedContractVersion(lastVersion.id)
        }

        onClose()
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('contract_versions.header.pause_resume.resume_modal.error'),
        time: 3000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      title={i18n.t('contract_versions.header.pause_resume.resume_modal.new_title')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={handleSubmit}
      disableFocusTrap
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={isLoading}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            type="primary"
            disabled={!activityResumeDate || isLoading}
            submit
            loading={isLoading}
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <Spinner />
        </Box>
      ) : (
        <FirstStep
          nextAvailableDate={nextAvailableDate}
          activityResumeDate={activityResumeDate}
          setActivityResumeDate={setActivityResumeDate}
          onboardingEnabled={onboardingEnabled}
          setOnboardingEnabled={setOnboardingEnabled}
          selectedWorkflow={selectedWorkflow}
          setSelectedWorkflow={setSelectedWorkflow}
          isOpen={isOpen}
          onToggle={setIsOpen}
        />
      )}
    </Modal>
  )
}

export default ResumeContractModal
